import { useEffect, useRef, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowRight, Sparkles, QrCode, RefreshCw, Play } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Logo } from "@/components/Logo";
import { AiMenuGenerator } from "@/components/AiMenuGenerator";
import { useAuth } from "@/context/AuthContext";
import { PhoneMock } from "@/lib/landingData";
import { LivePhone } from "@/components/landing/LivePhone";
import { PricingV2, CompareV2 } from "@/components/landing/PricingBlocks";
import { EASE, reveal, WordReveal, Parallax } from "@/components/landing/motion";
import { DemoVideoDialog } from "@/components/landing/DemoVideoDialog";
import { ProofStrip, Pillars, Extras, Audiences, Faq, FinalCta } from "@/components/landing/ValueBlocks";

const steps = [
  { n: "01", icon: Sparkles, title: "Crea il menù", text: "Scrivi i piatti o incolla il menù di carta: l'AI fa il resto." },
  { n: "02", icon: QrCode, title: "Stampa il QR", text: "Un codice per tutti i tavoli, un link per i social." },
  { n: "03", icon: RefreshCw, title: "Incassa e aggiorna", text: "Ordini, pagamenti e prezzi in tempo reale. Senza ristampe." },
];

export default function Landing() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [scrolled, setScrolled] = useState(false);
  const [demoOpen, setDemoOpen] = useState(false);
  const heroRef = useRef(null);
  const { scrollYProgress: heroP } = useScroll({ target: heroRef, offset: ["start start", "end start"] });
  const textY = useTransform(heroP, [0, 1], [0, -140]);
  const textOpacity = useTransform(heroP, [0, 0.55], [1, 0]);
  const phoneScale = useTransform(heroP, [0, 1], [1, 0.86]);
  const phoneY = useTransform(heroP, [0, 1], [0, 120]);
  const phoneRotate = useTransform(heroP, [0, 1], [0, -4]);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 20);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const goStart = () => navigate(user ? (user.role === "admin" ? "/admin" : "/dashboard") : "/register");

  return (
    <div className="min-h-screen bg-white text-gray-900 [overflow-x:clip]" data-testid="landing-v2">
      <DemoVideoDialog open={demoOpen} onOpenChange={setDemoOpen} />
      <header className={`fixed top-0 inset-x-0 z-50 transition-all duration-300 ${scrolled ? "mo-glass" : ""}`}>
        <div className="max-w-7xl mx-auto px-6 h-16 flex items-center justify-between">
          <Logo />
          <nav className="hidden md:flex items-center gap-8">
            <a href="#perche" className="text-sm font-medium text-gray-600 transition-colors hover:text-gray-900">Perché</a>
            <a href="#funzioni" className="text-sm font-medium text-gray-600 transition-colors hover:text-gray-900">Funzioni</a>
            <a href="#prezzi" className="text-sm font-medium text-gray-600 transition-colors hover:text-gray-900">Prezzi</a>
            <a href="#faq" className="text-sm font-medium text-gray-600 transition-colors hover:text-gray-900">FAQ</a>
            <Link to="/servizi" className="text-sm font-medium text-gray-600 transition-colors hover:text-gray-900" data-testid="nav-servizi">Servizi</Link>
          </nav>
          <div className="flex items-center gap-3">
            <Link to="/login" className="text-sm font-medium text-gray-700 transition-colors hover:text-gray-900" data-testid="nav-login">Accedi</Link>
            <Button onClick={goStart} className="rounded-full bg-gray-900 hover:bg-gray-800 text-white font-semibold px-5 active:scale-95 transition-transform" data-testid="nav-cta">Inizia ora</Button>
          </div>
        </div>
      </header>

      {/* Hero asimmetrico */}
      <section ref={heroRef} className="relative lg:h-[150vh]">
        <div className="lg:sticky lg:top-0 lg:h-screen flex items-center pt-28 pb-16 lg:py-0 overflow-hidden">
        <div className="absolute inset-0 mo-grid opacity-60 [mask-image:radial-gradient(ellipse_at_top,black,transparent_70%)]" />
        <div className="relative max-w-7xl mx-auto px-6 w-full grid lg:grid-cols-[1.15fr_1fr] gap-16 items-center">
          <motion.div style={{ y: textY, opacity: textOpacity }}>
            <motion.div initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} className="inline-flex items-center gap-2 rounded-full border border-gray-200 bg-white px-4 py-1.5 mb-8 shadow-sm">
              <span className="h-1.5 w-1.5 rounded-full bg-[#5aa469] animate-pulse" />
              <span className="text-xs text-gray-600 tracking-wide">Menù digitale, ordini e prenotazioni per il tuo locale</span>
            </motion.div>
            <motion.h1 initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.1, duration: 0.9, ease: EASE }} className="font-display font-extrabold text-5xl sm:text-6xl lg:text-7xl xl:text-[5.25rem] leading-[0.98] tracking-tight mb-7">
              Crea il tuo menù e <span className="text-[#5aa469]">gestisci gli ordini</span>. Da un solo QR.
            </motion.h1>
            <motion.p initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.2, duration: 0.9, ease: EASE }} className="text-gray-600 text-lg lg:text-xl max-w-xl mb-10 leading-relaxed">
              MenuOne è il menù digitale con QR code per ristoranti, pizzerie, bar e food truck. Menù gratis in 5 minuti; ordini, pagamento del conto e asporto quando vuoi.
            </motion.p>
            <motion.div initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.3, duration: 0.9, ease: EASE }} className="flex flex-wrap gap-3">
              <Button onClick={goStart} size="lg" className="rounded-full bg-[#5aa469] hover:bg-[#4a8a56] text-white font-semibold px-8 h-12 text-base group active:scale-95 transition-transform mo-glow" data-testid="hero-cta">
                Crea il menù gratis <ArrowRight className="ml-2 h-4 w-4 group-hover:translate-x-1 transition-transform" />
              </Button>
              <a href="#prezzi">
                <Button size="lg" variant="outline" className="rounded-full border-gray-300 bg-white text-gray-900 hover:bg-gray-50 px-8 h-12 text-base" data-testid="hero-pricing">Vedi i prezzi</Button>
              </a>
              <Button onClick={() => setDemoOpen(true)} size="lg" variant="ghost" className="rounded-full text-gray-900 hover:bg-gray-100 px-5 h-12 text-base group" data-testid="hero-demo">
                <span className="h-8 w-8 rounded-full bg-gray-900 text-white flex items-center justify-center mr-2.5 group-hover:scale-110 transition-transform"><Play className="h-3.5 w-3.5 ml-0.5" /></span>
                Guarda come funziona
              </Button>
            </motion.div>
          </motion.div>
          <motion.div initial={{ opacity: 0, scale: 0.9, y: 60 }} animate={{ opacity: 1, scale: 1, y: 0 }} transition={{ delay: 0.25, duration: 1.1, ease: EASE }} className="pt-8 lg:pt-0">
            <motion.div style={{ scale: phoneScale, y: phoneY, rotate: phoneRotate }}>
              <LivePhone />
            </motion.div>
          </motion.div>
        </div>
        </div>
      </section>

      {/* Come funziona */}
      <ProofStrip />
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-6">
          <motion.p {...reveal()} className="text-[#5aa469] text-xs tracking-[0.25em] uppercase mb-10">Come funziona</motion.p>
          <div className="grid md:grid-cols-3 gap-6">
          {steps.map((s, i) => (
            <motion.div key={s.n} {...reveal(i * 0.12)} className="flex gap-5" data-testid={`step-${i}`}>
              <span className="font-display font-extrabold text-5xl text-[#5aa469]/30 leading-none">{s.n}</span>
              <div>
                <div className="flex items-center gap-2 mb-1"><s.icon className="h-4 w-4 text-[#5aa469]" /><h3 className="font-display font-semibold text-xl">{s.title}</h3></div>
                <p className="text-gray-600 text-sm leading-relaxed">{s.text}</p>
              </div>
            </motion.div>
          ))}
          </div>
        </div>
      </section>

      {/* Perché */}
      <section id="perche" className="py-28 bg-[#FAFBFA] border-y border-gray-100">
        <div className="max-w-7xl mx-auto px-6 grid lg:grid-cols-[1fr_1.2fr] gap-12 items-end">
          <motion.div {...reveal()}>
            <p className="text-[#5aa469] text-xs tracking-[0.25em] uppercase mb-4">Perché MenuOne?</p>
            <h2 className="font-display font-bold text-4xl sm:text-5xl lg:text-6xl leading-[1.02] tracking-tight">
              Meno attese ai tavoli, più tempo per la cucina
            </h2>
          </motion.div>
          <WordReveal testid="perche-text" className="text-gray-900 font-medium text-xl lg:text-2xl leading-relaxed lg:pb-2 lg:border-l lg:border-gray-200 lg:pl-10"
            text="Il cliente legge, ordina e paga dal telefono. Il personale porta i piatti invece di correre tra i tavoli con il POS. Tu aggiorni il menù in un tocco, senza ristampe." />
        </div>
      </section>

      <Pillars />
      <Extras />
      <Audiences goStart={goStart} />

      {/* QR banner */}
      <section className="py-28 overflow-hidden">
        <div className="max-w-7xl mx-auto px-6 grid lg:grid-cols-2 gap-14 items-center">
          <div className="order-2 lg:order-1 flex justify-center gap-4 sm:gap-5">
            <Parallax speed={40}><PhoneMock variant="c" delay="0s" className="w-36 sm:w-44 rotate-[-6deg]" /></Parallax>
            <Parallax speed={-50}><PhoneMock variant="b" delay="0.7s" className="w-36 sm:w-44 rotate-[6deg] mt-10" /></Parallax>
          </div>
          <motion.div {...reveal(0.1)} className="order-1 lg:order-2">
            <h2 className="font-display font-bold text-4xl sm:text-5xl lg:text-6xl leading-[1.02] tracking-tight mb-6">
              Il menù sui social: i clienti scelgono prima di entrare
            </h2>
            <p className="text-gray-600 text-lg leading-relaxed mb-8">
              Metti il link su Instagram, Google e WhatsApp. Il menù è sempre leggibile dal telefono e sempre uguale a quello sul tavolo.
            </p>
            <Button onClick={goStart} className="rounded-full bg-gray-900 hover:bg-gray-800 text-white font-semibold px-7 h-11 active:scale-95 transition-transform" data-testid="qr-cta">Prova gratis</Button>
          </motion.div>
        </div>
      </section>

      <AiMenuGenerator />
      <PricingV2 goStart={goStart} />
      <CompareV2 goStart={goStart} />
      <Faq />
      <FinalCta goStart={goStart} />

      <footer id="contatti" className="border-t border-gray-100 py-12 bg-white">
        <div className="max-w-7xl mx-auto px-6 flex flex-col md:flex-row items-center justify-between gap-6">
          <div className="flex flex-col items-center md:items-start gap-3">
            <Logo />
            <p className="text-sm text-gray-500">© {new Date().getFullYear()} MenuOne. Tutti i diritti riservati.</p>
          </div>
          <div className="flex items-center gap-5 text-sm">
            <Link to="/privacy" className="text-gray-600 hover:text-[#5aa469] transition-colors" data-testid="footer-privacy">Privacy Policy</Link>
            <span className="text-gray-300">·</span>
            <Link to="/termini" className="text-gray-600 hover:text-[#5aa469] transition-colors" data-testid="footer-terms">Termini e Condizioni</Link>
          </div>
          <div className="text-center md:text-right">
            <p className="text-sm text-gray-500 mb-1">Informazioni e assistenza</p>
            <a href="mailto:creamenuone@gmail.com" className="text-[#5aa469] font-medium hover:underline" data-testid="contact-email">creamenuone@gmail.com</a>
          </div>
        </div>
      </footer>
    </div>
  );
}
